/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { memo } from 'react';

import { cssMap, jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';

import Button, { IconButton } from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/core/close';
import { Inline, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { type AIAction } from '@atlassian/ai-smart-button-api';

export const i18n = defineMessages({
	aiProactiveNudgeDismiss: {
		id: 'ai-smart-button.ai-proactive-nudge.dismiss',
		defaultMessage: 'Dismiss',
		description: 'The label text for the dismiss button on the AI Proactive nudge',
	},
});

const ANIMATION_DURATION = 200;

const styles = cssMap({
	wrapperStyles: {
		position: 'absolute',
		left: 0,
		'@media (prefers-reduced-motion)': {
			animation: 'none',
		},
		paddingTop: token('space.050'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
		borderRadius: token('border.radius.200'),
		display: 'inline-block',
		boxShadow: token('elevation.shadow.overlay'),
		backgroundColor: token('elevation.surface.raised'),
		transition: `opacity ${ANIMATION_DURATION}ms ease-in-out, transform ${ANIMATION_DURATION}ms ease-in-out`,
	},
	invisibleNudge: {
		opacity: 0,
		transform: `translateX(-100%)`,
	},
	visibleNudge: {
		transform: `translateX(calc(-100% - ${token('space.100')}))`,
		opacity: 1,
	},
	descriptionStyles: {
		paddingRight: token('space.150'),
	},
});

type NudgeContentProps = {
	activeNudge: AIAction;
	isNudgeVisible: boolean;
	onWrapperMouseEnter: () => void;
	onWrapperMouseLeave: () => void;
	onNudgeClick: () => void;
	onDismissClick: () => void;
};

export const NudgeContent = memo(
	({
		activeNudge,
		isNudgeVisible,
		onWrapperMouseEnter,
		onWrapperMouseLeave,
		onNudgeClick,
		onDismissClick,
	}: NudgeContentProps) => {
		const intl = useIntl();

		return (
			/**
			 * Wrapper div is used to handle mouse enter and leave events
			 *  because dismiss timer needs to be stopped when
			 * 	user hover over anywhere in nudge.
			 *  Listnening only on button or icon, will not stop timer when
			 * 	hovering over th edges of the nudge.
			 */
			// eslint-disable-next-line jsx-a11y/no-static-element-interactions
			<div
				css={[styles.wrapperStyles, isNudgeVisible ? styles.visibleNudge : styles.invisibleNudge]}
				data-testid="platform-ai-proactive-nudge"
				onMouseEnter={onWrapperMouseEnter}
				onMouseLeave={onWrapperMouseLeave}
			>
				<Inline grow="fill" space="space.025">
					<Stack alignInline="end">
						<Button iconBefore={() => activeNudge.icon} appearance="subtle" onClick={onNudgeClick}>
							{activeNudge.title}
						</Button>
						<Inline xcss={styles.descriptionStyles}>
							{activeNudge.description ? activeNudge.description : null}
						</Inline>
					</Stack>
					<IconButton
						testId="platform-ai-proactive-nudge-dismiss"
						appearance="subtle"
						label={intl.formatMessage(i18n.aiProactiveNudgeDismiss)}
						isTooltipDisabled={false}
						tooltip={{ position: 'top' }}
						icon={(iconProps) => <CrossIcon {...iconProps} size="small" />}
						onClick={onDismissClick}
					/>
				</Inline>
			</div>
		);
	},
);
